import { Badge, BadgeLevel, BadgeShape, BadgeSize, Button, ButtonLevel } from '@vizit/atoms';
import { ScoreStickerV2, ScoreStickerV2Size, Text, TextColor, TextSize, TextWeight } from '@vizit/atoms';
import { NEEDS_CATEGORY_SCORE_LABEL } from '@vizit/constants';
import { XCloseIcon } from '@vizit/icons';
import { Asset, type AssetImageOverlayProps } from '@vizit/molecules';
import { cx } from '@vizit/theme';
import { getScoreDescription } from '@vizit/utils';

interface CompareCardProps {
  name: string;
  imageSrc: string | null;
  fallbackSrc?: string | null;
  heatmapOverlay?: AssetImageOverlayProps;
  score: number | null;
  isScoring?: boolean;
  /** No scoring can run without a category; the card says so instead of "Unscored" */
  needsCategory?: boolean;
  imageTypeLabel: string;
  isHighest?: boolean;
  onRemove?: () => void;
  onOpen?: () => void;
  className?: string;
}

function scoreLabel(score: number | null, isScoring: boolean, needsCategory: boolean): string {
  if (isScoring) return 'Scoring…';
  if (score !== null) return getScoreDescription(score);
  return needsCategory ? NEEDS_CATEGORY_SCORE_LABEL : 'Unscored';
}

function CompareCard(props: CompareCardProps) {
  const { name, imageSrc, fallbackSrc, heatmapOverlay, score, isScoring = false, needsCategory = false } = props;
  const { imageTypeLabel } = props;
  const { isHighest = false, onRemove, onOpen, className } = props;

  return (
    <article aria-label={name} className={cx('flex min-w-0 flex-col gap-2', className)}>
      <div className="flex h-18 items-center gap-4">
        <Text size={TextSize.Xs} weight={TextWeight.Medium} color={TextColor.Secondary} className="min-w-0">
          {name}
        </Text>
        {isHighest && (
          <Badge level={BadgeLevel.Success} shape={BadgeShape.Box} size={BadgeSize.Sm} className="shrink-0">
            Highest score
          </Badge>
        )}
        {onRemove && (
          <Button
            aria-label={`Remove ${name} from comparison`}
            level={ButtonLevel['Text-Secondary']}
            className="ml-auto shrink-0"
            onPress={onRemove}
          >
            <XCloseIcon size={14} />
          </Button>
        )}
      </div>
      <Asset showScoreBar={false} isViewOnly={!onOpen} onPress={onOpen} aria-label={`Open ${name}`}>
        <Asset.Image border name={name} src={imageSrc} fallbackSrc={fallbackSrc} overlay={heatmapOverlay} />
      </Asset>
      <div className="flex items-center gap-7 pt-2">
        <ScoreStickerV2 score={isScoring ? null : score} size={ScoreStickerV2Size.Sm} isLoading={isScoring} />
        <div className="flex min-w-0 flex-col gap-2">
          <Text size={TextSize.Md} weight={TextWeight.Medium} color={TextColor.Primary}>
            {scoreLabel(score, isScoring, needsCategory)}
          </Text>
          <Text size={TextSize.Sm} color={TextColor.Tertiary}>
            {imageTypeLabel}
          </Text>
        </div>
      </div>
    </article>
  );
}

export { CompareCard };
export type { CompareCardProps };
